import { useEffect, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Download, FileIcon, MessageSquare, EyeOff } from "lucide-react";
import { getLocalStorage, setLocalStorage } from "@/lib/utils/local-storage";
import { SidebarCollapseToggle } from "../../pages/job-page/job-page-components/sidebar-collapse-toggle.component";
import { SIDEBAR_COLLAPSED_WIDTH } from "../../pages/job-page/job-page-components/job-page.constants";
import { supabase } from "@/lib/supabase";
import {
	JobWithShare,
	FileWithShare,
	PatientEntityType,
} from "@/lib/supabase/supabaseTypes";
import { ChatComponent } from "../../components/chat/chat.component";
import { Loading } from "@/components/src/animations/loading";
import { VendorJobPatientInfo } from "./vendor-job-patient-info.component";
import { VendorJobFiles } from "./vendor-job-files.component";
import { VendorJobDeliveries } from "./vendor-job-deliveries.component";
import { useFileHandler } from "@/lib/hooks/FileHandler/useFileHandler";
import {
	getFileType,
	getFileTypeWithDocumentType,
} from "@/lib/utils/file-utils";
import { FileContent } from "@/components/src/file-viewer/file-content.component";
import { VendorPdfPreview } from "./vendor-pdf-preview.component";
import { useStorage } from "../../hooks/useStorage";
import { useVendorJobStore } from "../../store/VendorJobs";

interface VendorJobPageProps {
	organizationId: string;
	canMarkDelivered?: boolean;
}

export const VendorJobPage: React.FC<VendorJobPageProps> = ({
	organizationId,
	canMarkDelivered,
}) => {
	const { vendorId, jobId } = useParams<{
		vendorId: string;
		jobId: string;
	}>();
	const [searchParams] = useSearchParams();
	const initialFileId = searchParams.get("file");
	const processedFileParam = useRef<string | null>(null);

	const [job, setJob] = useState<JobWithShare | null>(null);
	const [patient, setPatient] = useState<PatientEntityType | null>(null);
	const [sharedFiles, setSharedFiles] = useState<FileWithShare[]>([]);
	const [loading, setLoading] = useState(true);
	const [selectedFileId, setSelectedFileId] = useState<string | null>(null);
	const [chatCollapsed, setChatCollapsed] = useState(() =>
		getLocalStorage("vendor-job-chat-collapsed", false)
	);

	const handleChatToggle = () => {
		setChatCollapsed((prev) => {
			const next = !prev;
			setLocalStorage("vendor-job-chat-collapsed", next);
			return next;
		});
	};

	const { downloadFromSupabase } = useStorage();

	const fileIds = sharedFiles.map((f) => f.id).filter(Boolean) as string[];
	const {
		files,
		loading: filesLoading,
		sharedUntil,
	} = useFileHandler(fileIds);

	useEffect(() => {
		const fetchVendorJobData = async () => {
			if (!jobId || !vendorId || !organizationId) return;

			setLoading(true);
			setPatient(null);
			setSelectedFileId(null);
			processedFileParam.current = null;

			const { data: jobData, error: jobError } = await supabase
				.from("jobs_with_shares")
				.select("*")
				.eq("id", Number(jobId))
				.eq("organization_id", vendorId)
				.eq("shared_with", organizationId)
				.single();

			if (jobError || !jobData) {
				setJob(null);
				setLoading(false);
				return;
			}

			setJob(jobData);

			const [patientResult, filesResult] = await Promise.all([
				jobData.patient_id
					? supabase
							.from("patients")
							.select("*")
							.eq("id", jobData.patient_id)
							.single()
					: Promise.resolve({ data: null }),
				supabase.rpc("get_shared_files_for_job", {
					p_job_id: Number(jobId),
				}),
			]);

			if (patientResult.data) {
				setPatient(patientResult.data);
			}

			if (filesResult.data) {
				setSharedFiles(filesResult.data);
			}
			setLoading(false);
		};

		fetchVendorJobData();
	}, [jobId, vendorId, organizationId]);

	useEffect(() => {
		if (files.length === 0) return;

		if (initialFileId && initialFileId !== processedFileParam.current) {
			processedFileParam.current = initialFileId;
			const targetFile = files.find((f) => f.id === initialFileId);
			if (targetFile) {
				setSelectedFileId(initialFileId);
				return;
			}
		}

		if (selectedFileId) return;

		const sortedByDate = [...files].sort(
			(a, b) =>
				new Date(b.created_at || 0).getTime() -
				new Date(a.created_at || 0).getTime()
		);

		const getType = (f: (typeof files)[0]) =>
			getFileTypeWithDocumentType(
				f.meta_data as Record<string, any> | null,
				f.file_name
			);

		const ls = sortedByDate.find((f) => getType(f) === "ls");
		if (ls) {
			setSelectedFileId(ls.id);
			return;
		}

		const kv = sortedByDate.find((f) => getType(f) === "kv");
		if (kv) {
			setSelectedFileId(kv.id);
			return;
		}

		setSelectedFileId(sortedByDate[0]?.id || null);
	}, [files, selectedFileId, initialFileId]);

	const setActiveFileId = useVendorJobStore((state) => state.setActiveFileId);
	useEffect(() => {
		setActiveFileId(selectedFileId);
	}, [selectedFileId, setActiveFileId]);

	const selectedFile = files.find((f) => f.id === selectedFileId);
	const selectedFileUnshared =
		selectedFileId && sharedUntil[selectedFileId] != null;
	const selectedFileType = selectedFile
		? getFileType(
				selectedFile.meta_data as Record<string, any> | null,
				selectedFile.file_name
			)
		: null;

	const handleDownload = () => {
		if (selectedFile?.path_name) {
			downloadFromSupabase(selectedFile.path_name);
		}
	};

	if (loading) {
		return <Loading />;
	}

	if (!job) {
		return (
			<div className="p-4">
				<p className="text-gray-500">Auftrag nicht gefunden</p>
			</div>
		);
	}

	const handleShareFile = async (fileId: string) => {
		await supabase.rpc("share_file", {
			p_share_file_id: fileId,
			p_message_content: { type: "shared_file_v2", file_id: fileId },
		});
	};

	const handleUnshareFile = async (fileId: string) => {
		await supabase.rpc("unshare_file", { unshare_file_id: fileId });
	};

	return (
		<div className="h-full flex">
			<div
				className="h-full border-r border-stone-200 flex flex-col transition-[width] duration-200"
				style={{ width: chatCollapsed ? SIDEBAR_COLLAPSED_WIDTH : 400 }}
			>
				{chatCollapsed ? (
					<button
						onClick={handleChatToggle}
						className="flex-1 flex items-center justify-center"
						aria-label="Chat öffnen"
						data-testid="vendor-job-chat-expand"
					>
						<MessageSquare size={20} className="text-stone-400" />
					</button>
				) : (
					<>
						<div className="px-4 py-2 border-b border-stone-200 flex-shrink-0">
							<div className="font-semibold text-stone-800">
								Chat & Historie
							</div>
						</div>
						<div className="flex-1 min-h-0">
							{organizationId && (
								<ChatComponent
									channelId={job.channel_id ?? null}
									organizationId={organizationId}
									onShareFile={handleShareFile}
									onUnshareFile={handleUnshareFile}
									allowGoTo={false}
									jobId={job.id ?? undefined}
									vendorOrgId={vendorId}
									customerOrgId={organizationId}
								/>
							)}
						</div>
					</>
				)}
				<SidebarCollapseToggle
					isCollapsed={chatCollapsed}
					onToggle={handleChatToggle}
					testId="vendor-job-chat-toggle"
				/>
			</div>

			<div className="w-[350px] h-full overflow-y-auto">
				<div className="p-4 space-y-4">
					<div>
						<h2 className="text-lg font-semibold text-stone-800">
							{job.title || "Auftrag"}
						</h2>
						{job.code && (
							<p className="text-sm text-stone-500 mt-1">
								Code: {job.code}
							</p>
						)}
					</div>

					{patient && <VendorJobPatientInfo patient={patient} />}

					<VendorJobFiles
						files={files}
						loading={filesLoading}
						selectedFileId={selectedFileId}
						onSelectFile={setSelectedFileId}
						sharedUntil={sharedUntil}
					/>

					{job.id && (
						<VendorJobDeliveries
							jobId={job.id}
							canMarkDelivered={canMarkDelivered}
						/>
					)}
				</div>
			</div>

			<div className="flex-1 min-w-0 h-full flex flex-col bg-stone-200/60">
				{selectedFile && !selectedFileUnshared && (
					<div className="flex items-center justify-between px-4 py-2 border-b border-stone-200 flex-shrink-0">
						<span className="text-sm text-stone-600 truncate">
							{selectedFile.file_name}
						</span>
						<button
							onClick={handleDownload}
							className="flex items-center gap-1.5 text-sm text-stone-500 hover:text-stone-800 transition-colors"
							aria-label="Datei herunterladen"
							data-testid="vendor-job-files-download"
						>
							<Download size={14} />
							<span>Herunterladen</span>
						</button>
					</div>
				)}
				<div className="flex-1 min-h-0">
					{selectedFileUnshared ? (
						<div className="flex flex-col items-center justify-center h-full gap-2">
							<EyeOff size={32} className="text-stone-300" />
							<span className="text-sm text-stone-400">
								Datei nicht mehr geteilt
							</span>
						</div>
					) : selectedFile?.url ? (
						selectedFileType === "pdf" ? (
							<VendorPdfPreview url={selectedFile.url} />
						) : (
							<FileContent
								url={selectedFile.url}
								fileType={selectedFileType}
								fileName={selectedFile.file_name || undefined}
								isLoading={filesLoading}
							/>
						)
					) : (
						<div className="flex flex-col items-center justify-center h-full gap-2">
							<FileIcon size={32} className="text-stone-300" />
							<span className="text-sm text-stone-400">
								{filesLoading
									? "Dateien werden geladen..."
									: "Wählen Sie eine Datei aus"}
							</span>
						</div>
					)}
				</div>
			</div>
		</div>
	);
};
